import { useEffect, useRef } from "react";

/**
 * OndiWidget — mascota rediseñada para el floater (Fase de rediseño visual).
 *
 * Diseño (según referencia del usuario):
 *   - Robot chibi azul metálico eléctrico, glow cyan alrededor.
 *   - Ojos LED anillados con parpadeo periódico.
 *   - Boca dot-matrix de sonrisa cyan (compresión sutil al hablar).
 *   - Emblema triangular cyan brillante en el pecho (pulsante).
 *   - Manitas cyan con animación de saludo al hacer `waving`.
 *   - Cuerpo con bobbing vivo + partículas cyan orbitando + aura pulsante.
 *
 * 100% SVG + CSS keyframes (transform/opacity), sin dependencias, respeta
 * prefers-reduced-motion desactivando lo no esencial. Drop-in del OndiCharacter
 * anterior con la misma API (size / waving / talking).
 */
export function OndiWidget({
  width = 76,
  height = 92,
  waving = false,
  talking = false,
  className = "",
  style = {},
}) {
  const wrapperRef = useRef(null);

  // Randomize particle delays once per mount so multiple mascots aren't sync'd.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.style.setProperty("--ondi-delay-1", `${Math.random() * 1.5}s`);
    el.style.setProperty("--ondi-delay-2", `${Math.random() * 1.5}s`);
    el.style.setProperty("--ondi-delay-3", `${Math.random() * 1.5}s`);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={"ondi-widget " + className}
      style={{ width, height, ...style }}
      data-testid="ondi-mascot"
      data-waving={waving ? "true" : "false"}
      data-talking={talking ? "true" : "false"}
      aria-hidden="true"
    >
      {/* Aura pulsing behind */}
      <div className="ondi-widget-aura" />

      {/* Orbiting particles */}
      <span className="ondi-widget-particle ondi-widget-p1" />
      <span className="ondi-widget-particle ondi-widget-p2" />
      <span className="ondi-widget-particle ondi-widget-p3" />

      {/* Bobbing body */}
      <div className="ondi-widget-body">
        <svg
          viewBox="0 0 100 120"
          width={width}
          height={height}
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          className="ondi-widget-svg"
        >
          <defs>
            <radialGradient id="ow-head" cx="35%" cy="26%" r="72%">
              <stop offset="0%" stopColor="#6b98cf" />
              <stop offset="55%" stopColor="#2f5580" />
              <stop offset="100%" stopColor="#173355" />
            </radialGradient>
            <radialGradient id="ow-body" cx="35%" cy="26%" r="82%">
              <stop offset="0%" stopColor="#5b86bc" />
              <stop offset="55%" stopColor="#284d74" />
              <stop offset="100%" stopColor="#132a48" />
            </radialGradient>
            <linearGradient id="ow-face" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0d1f38" />
              <stop offset="100%" stopColor="#050e1c" />
            </linearGradient>
            <radialGradient id="ow-emblem" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#e3faff" />
              <stop offset="55%" stopColor="#4de0ff" />
              <stop offset="100%" stopColor="#0aa9c9" />
            </radialGradient>
            <filter id="ow-neon" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="ow-neon-strong" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="2.4" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* ---- Body group ---- */}
          <g>
            {/* Shoulder pads */}
            <ellipse cx="24" cy="78" rx="6" ry="7" fill="url(#ow-body)" stroke="#22c7e2" strokeWidth="0.6" strokeOpacity="0.55" />
            <ellipse cx="76" cy="78" rx="6" ry="7" fill="url(#ow-body)" stroke="#22c7e2" strokeWidth="0.6" strokeOpacity="0.55" />

            {/* Torso */}
            <path
              d="M 30 62 Q 24 62 23 74 L 23 100 Q 23 112 40 114 L 60 114 Q 77 112 77 100 L 77 74 Q 76 62 70 62 Z"
              fill="url(#ow-body)"
              stroke="#22c7e2"
              strokeWidth="1"
              strokeOpacity="0.7"
            />

            {/* Chest emblem triangle */}
            <g className="ondi-emblem" filter="url(#ow-neon-strong)">
              <polygon
                points="50,74 41,90 59,90"
                fill="url(#ow-emblem)"
                stroke="#a3f0ff"
                strokeWidth="0.4"
              />
              <polygon points="50,78 45,88 55,88" fill="#e3faff" opacity="0.55" />
            </g>

            {/* Left hand — resting */}
            <g>
              <circle cx="17" cy="93" r="4.5" fill="#2f5580" stroke="#22c7e2" strokeWidth="0.55" />
              <circle cx="17" cy="93" r="1.9" fill="#4de0ff" filter="url(#ow-neon)" />
            </g>

            {/* Right hand — waves when `waving` is true */}
            <g className="ondi-hand-r" style={{ transformOrigin: "83px 88px" }}>
              <circle cx="83" cy="93" r="4.5" fill="#2f5580" stroke="#22c7e2" strokeWidth="0.55" />
              <circle cx="83" cy="93" r="1.9" fill="#4de0ff" filter="url(#ow-neon)" />
            </g>
          </g>

          {/* ---- Head group ---- */}
          <g>
            {/* Head base */}
            <path
              d="M 22 20 Q 22 6 40 6 L 60 6 Q 78 6 78 20 L 78 46 Q 78 60 60 60 L 40 60 Q 22 60 22 46 Z"
              fill="url(#ow-head)"
              stroke="#22c7e2"
              strokeWidth="1"
              strokeOpacity="0.7"
            />

            {/* Ear/side pods */}
            <ellipse cx="18" cy="33" rx="3" ry="5" fill="#284d74" stroke="#22c7e2" strokeWidth="0.5" strokeOpacity="0.55" />
            <ellipse cx="82" cy="33" rx="3" ry="5" fill="#284d74" stroke="#22c7e2" strokeWidth="0.5" strokeOpacity="0.55" />

            {/* Head top highlight */}
            <path
              d="M 28 14 Q 40 8 60 8 Q 72 8 74 15"
              fill="none"
              stroke="#8ebde3"
              strokeWidth="0.9"
              strokeOpacity="0.4"
              strokeLinecap="round"
            />

            {/* Face plate (dark screen) */}
            <path
              d="M 28 20 Q 28 16 34 16 L 66 16 Q 72 16 72 20 L 72 46 Q 72 52 66 52 L 34 52 Q 28 52 28 46 Z"
              fill="url(#ow-face)"
              stroke="#22c7e2"
              strokeWidth="0.6"
              strokeOpacity="0.45"
            />

            {/* Eyes — LED rings */}
            <g filter="url(#ow-neon)">
              <g className="ondi-eye ondi-eye-l">
                <circle cx="40" cy="32" r="5.5" fill="none" stroke="#4de0ff" strokeWidth="1.8" />
                <circle cx="40" cy="32" r="2.8" fill="#4de0ff" opacity="0.35" />
                <circle cx="41.3" cy="30.7" r="1.2" fill="#eaffff" />
              </g>
              <g className="ondi-eye ondi-eye-r">
                <circle cx="60" cy="32" r="5.5" fill="none" stroke="#4de0ff" strokeWidth="1.8" />
                <circle cx="60" cy="32" r="2.8" fill="#4de0ff" opacity="0.35" />
                <circle cx="61.3" cy="30.7" r="1.2" fill="#eaffff" />
              </g>
            </g>

            {/* Mouth — dot-matrix smile */}
            <g className="ondi-mouth" filter="url(#ow-neon)" style={{ transformOrigin: "50px 44px" }}>
              <circle cx="44" cy="43.2" r="0.85" fill="#4de0ff" />
              <circle cx="46.4" cy="43.9" r="0.85" fill="#4de0ff" />
              <circle cx="48.8" cy="44.4" r="0.85" fill="#4de0ff" />
              <circle cx="51.2" cy="44.4" r="0.85" fill="#4de0ff" />
              <circle cx="53.6" cy="43.9" r="0.85" fill="#4de0ff" />
              <circle cx="56" cy="43.2" r="0.85" fill="#4de0ff" />
            </g>
          </g>
        </svg>
      </div>

      {/* Colocated style — component-scoped via classes, no CSS module needed */}
      <style>{`
        .ondi-widget {
          position: relative;
          display: block;
          filter: drop-shadow(0 4px 14px rgba(0,0,0,0.35));
        }
        .ondi-widget-aura {
          position: absolute;
          inset: -8%;
          border-radius: 50%;
          background:
            radial-gradient(circle at 50% 45%,
              rgba(77,224,255,0.55) 0%,
              rgba(77,224,255,0.22) 40%,
              rgba(77,224,255,0.0) 70%);
          filter: blur(4px);
          animation: ondi-aura-pulse 2.6s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }
        .ondi-widget-body {
          position: relative;
          width: 100%;
          height: 100%;
          animation: ondi-bob 3.6s ease-in-out infinite;
          z-index: 1;
        }
        .ondi-widget[data-talking="true"] .ondi-widget-body {
          animation-duration: 1.6s;
        }
        .ondi-widget-svg { display: block; }

        /* --- Eyes --- */
        .ondi-eye {
          transform-box: fill-box;
          transform-origin: center;
          animation: ondi-blink 5.2s ease-in-out infinite;
        }
        .ondi-eye-r { animation-delay: 0.04s; }

        /* --- Mouth (talking) --- */
        .ondi-mouth {
          transform-box: fill-box;
          transform-origin: center;
        }
        .ondi-widget[data-talking="true"] .ondi-mouth {
          animation: ondi-mouth-talk 0.36s ease-in-out infinite;
        }

        /* --- Emblem pulse --- */
        .ondi-emblem {
          transform-box: fill-box;
          transform-origin: center;
          animation: ondi-emblem-pulse 1.9s ease-in-out infinite;
        }

        /* --- Wave hand --- */
        .ondi-hand-r {
          transform-box: fill-box;
          transform-origin: 83px 88px;
          transition: transform 0.2s ease;
        }
        .ondi-widget[data-waving="true"] .ondi-hand-r {
          animation: ondi-hand-wave 0.9s ease-in-out infinite;
        }

        /* --- Particles --- */
        .ondi-widget-particle {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #4de0ff;
          box-shadow: 0 0 8px #4de0ff, 0 0 16px rgba(77,224,255,0.6);
          pointer-events: none;
          opacity: 0;
          z-index: 2;
        }
        .ondi-widget-p1 { --tx: 28px;  --ty: -22px; animation: ondi-particle 3.4s ease-in-out infinite; animation-delay: var(--ondi-delay-1, 0s); }
        .ondi-widget-p2 { --tx: -30px; --ty: -8px;  animation: ondi-particle 4.2s ease-in-out infinite; animation-delay: var(--ondi-delay-2, 0.6s); width: 4px; height: 4px; }
        .ondi-widget-p3 { --tx: 18px;  --ty: 18px;  animation: ondi-particle 3.7s ease-in-out infinite; animation-delay: var(--ondi-delay-3, 1.1s); width: 5px; height: 5px; }

        /* ==== keyframes ==== */
        @keyframes ondi-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes ondi-aura-pulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.08); }
        }
        @keyframes ondi-blink {
          0%, 92%, 100% { transform: scaleY(1); }
          95%, 96.5%    { transform: scaleY(0.06); }
        }
        @keyframes ondi-mouth-talk {
          0%, 100% { transform: scaleX(1); }
          50%      { transform: scaleX(0.5); }
        }
        @keyframes ondi-emblem-pulse {
          0%, 100% { opacity: 0.9; }
          50%      { opacity: 1; }
        }
        @keyframes ondi-hand-wave {
          0%, 100% { transform: rotate(0); }
          25%      { transform: rotate(20deg); }
          75%      { transform: rotate(-14deg); }
        }
        @keyframes ondi-particle {
          0%   { opacity: 0; transform: translate(0,0) scale(0.3); }
          25%  { opacity: 1; }
          80%  { opacity: 0.5; }
          100% { opacity: 0; transform: translate(var(--tx, 20px), var(--ty, -20px)) scale(0.9); }
        }

        /* Reduced motion → keep only essential vibe (eye blink + emblem pulse). */
        @media (prefers-reduced-motion: reduce) {
          .ondi-widget-body,
          .ondi-widget-aura,
          .ondi-widget-particle,
          .ondi-hand-r,
          .ondi-mouth {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
